import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { getWithSelector } from '@lumapps/lumx/core/utils/selectors';
import { mdiClose } from '@lumapps/lumx/icons';
import { Chip, ChipGroup, Icon, Tooltip, type Theme } from '@lumapps/lumx/react';
import type { Selector } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isComponentType } from '@lumapps/utils/types/isComponentType';

import { useFocusLastChipOnBackspace } from '../../hooks/useFocusLastChipOnBackspace';
import { LUMX_INPUTS } from '../../keys';

import './index.scss';

export interface SelectionChipGroupProps<O> {
    /**
     * Option object id selector (either the property name or a function to get the id)
     */
    getOptionId: Selector<O>;
    /**
     * Option object name selector (either the property name or a function to get the name)
     * Fallbacks on the ID if not defined
     */
    getOptionName?: Selector<O, string | undefined | null>;
    /**
     * Selected options array
     */
    value?: O[];
    /**
     * Callback on option array selected
     */
    onChange?(newValue?: O[]): void;
    /**
     * Input ref to restore focus
     */
    inputRef?: React.RefObject<HTMLInputElement>;
    /**
     * Input label, used to generate the chip group aria description
     */
    inputLabel?: string;
    /**
     * Customize how chips should render
     */
    renderChip?: (option: O) => React.ReactNode;
    /**
     * Scope for tracking purposes
     */
    scope?: string;
    /**
     * LumX theme
     */
    theme?: Theme;
    /**
     * Disabled state
     */
    isDisabled?: boolean;
}

const CLASSNAME = 'lumx-selection-chip-group';

/**
 * Selection chip group listing currently selected options in a chip list.
 *
 * Remove value on:
 * - Activated (click or Enter pressed)
 * - Backspace pressed (also moves focus on previous chip or the input)
 */
export const SelectionChipGroup = <O,>({
    onChange,
    value,
    getOptionId,
    getOptionName,
    inputRef,
    inputLabel,
    renderChip,
    scope = CLASSNAME,
    theme,
    isDisabled,
}: SelectionChipGroupProps<O>) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope);
    const chipRefs = React.useRef<React.RefObject<HTMLElement>[]>([]);
    const { findPreviousEnabledChip } = useFocusLastChipOnBackspace(chipRefs, inputRef);

    const label = translateAndReplace(LUMX_INPUTS.SELECTED_VALUES_LIST_LABEL, {
        SELECT_LABEL: inputLabel || translateKey(GLOBAL.VALUES),
    });
    return (
        <ChipGroup role="group" aria-label={label} className={block()}>
            {value?.map((v, index) => {
                const name = getWithSelector(getOptionName, v);
                const id = getWithSelector(getOptionId, v);
                const onClick = () => {
                    const newValue = [...value];
                    const existingIndex = value.findIndex((v) => getWithSelector(getOptionId, v) === id);
                    if (existingIndex === -1) {
                        return;
                    }
                    // Remove value
                    newValue.splice(existingIndex, 1);

                    onChange?.(newValue);
                };
                const onKeyDown = (evt: React.KeyboardEvent) => {
                    if (evt.key !== 'Backspace' && evt.key !== 'Enter') {
                        return;
                    }
                    evt.preventDefault();

                    // Activate (remove value) on Backspace pressed
                    onClick();

                    const previousChip = findPreviousEnabledChip(index - 1);
                    const input = inputRef?.current;

                    // Focus the previous chip or the input
                    (previousChip || input)?.focus();
                };

                if (!chipRefs.current[index]) {
                    chipRefs.current[index] = React.createRef<HTMLElement>();
                }
                const ref: React.Ref<HTMLElement> | undefined = chipRefs.current[index];

                const customChip = renderChip?.(v);
                const props = isComponentType(Chip, customChip) ? customChip.props : undefined;
                const chipIsDisabled = props?.isDisabled || isDisabled;
                const chipName = typeof props?.children === 'string' ? props.children : name;
                const tooltipLabel = `${chipName} - ${translateKey(GLOBAL.REMOVE)}`;

                return (
                    <Tooltip
                        key={id}
                        label={!chipIsDisabled ? tooltipLabel : undefined}
                        closeMode="hide"
                        ariaLinkMode="aria-labelledby"
                    >
                        <Chip
                            {...get({ element: 'chip', action: id })}
                            {...props}
                            after={<Icon icon={mdiClose} />}
                            className={element('chip', [props?.className])}
                            size="s"
                            ref={ref as any}
                            onClick={onClick}
                            onKeyDown={onKeyDown}
                            theme={theme}
                            isDisabled={chipIsDisabled}
                            tabIndex={chipIsDisabled ? -1 : 0}
                        >
                            {props?.children || name}
                        </Chip>
                    </Tooltip>
                );
            })}
        </ChipGroup>
    );
};
